import "../../App.css";

import type { AlertColor } from "@mui/material/Alert/Alert";
import { useQueryClient as useTanStackQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import { Route, Routes } from "react-router-dom";

import useApi from "../../api/apiConfig";
import { saveFile } from "../../api/files";
import useQueryClient from "../../api/query";
import { fileToSource } from "../../api/sources";
import {
  type BackendFilters,
  createDefaultBackendFilters,
  type DataGridFilter,
  FilterLogicalOperator,
  FilterOperator,
  validateBackendFilters,
} from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import { camelToSnakeCase } from "../../utils/utils";
import { validatePaymentAndNotify } from "../validation";
import type { PaymentsSearchParams } from "./api";
import {
  appendPaymentRow,
  applyPaymentsAction,
  type PaymentsCacheAction,
  useCreateAIEntity,
  useCreatePayment,
  usePayments,
  useUpdatePayment,
} from "./hooks";
import PaymentDetail from "./PaymentDetail";
import PaymentsListView, {
  type PaymentDashboardSettings,
} from "./PaymentsListView";

const DEFAULT_PAGE_SIZE = 10;
const NO_PAYMENTS: PaymentMessage[] = [];

interface PaymentsDashboardProps {
  selectedPractices: PracticeMessage[];
  allPractices: PracticeMessage[];
  dashboardSettings: PaymentDashboardSettings;
  saveDashboardSettings: (newSettings: PaymentDashboardSettings) => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
}

export default function PaymentsDashboard({
  selectedPractices,
  allPractices,
  dashboardSettings,
  saveDashboardSettings,
  updateSnackBar,
  supportedPayers,
  supportedSources,
}: PaymentsDashboardProps) {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  const rqClient = useTanStackQueryClient();
  const isDevMode = useDevMode();
  const createPaymentMutation = useCreatePayment();
  const updatePaymentMutation = useUpdatePayment();
  const createAIEntityMutation = useCreateAIEntity();

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const updateStore = (action: PaymentsCacheAction) =>
    applyPaymentsAction(rqClient, action);

  const formatFilterValue = (
    operator: string,
    value: unknown,
  ): string | string[] => {
    if (["isEmpty", "isNotEmpty"].includes(operator)) return "";

    if (operator === "isAnyOf" && Array.isArray(value)) {
      return value
        .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
        .map((v) => String(v).trim());
    }

    if (value instanceof Date) {
      return value.toISOString().split("T")[0];
    }

    return String(value || "");
  };

  const extractFiltersFromLocalStorage = (): BackendFilters => {
    try {
      const persistedState = localStorage.getItem("paymentsListView");
      if (!persistedState) {
        return createDefaultBackendFilters();
      }

      const parsedState = JSON.parse(persistedState);
      const filterItems = parsedState?.filter?.filterModel?.items || [];
      const datagridFilterItems: Array<DataGridFilter> = [];

      filterItems
        .filter(
          (item: { field: string; operator: string }) =>
            item.field && typeof item.operator === "string",
        )
        .forEach(
          (item: { field: string; operator: string; value: unknown }) => {
            const backendField = camelToSnakeCase(item.field);
            datagridFilterItems.push({
              field: backendField,
              operator: item.operator as FilterOperator,
              value: formatFilterValue(item.operator, item.value),
            });
          },
        );

      const result: BackendFilters = {
        filters: {},
        datagridFilters: datagridFilterItems,
        logicalOperator: FilterLogicalOperator.AND,
      };

      const errors = validateBackendFilters(result);
      if (errors.length > 0) {
        return createDefaultBackendFilters();
      }

      return result;
    } catch (_error) {
      return createDefaultBackendFilters();
    }
  };

  const [backendFilters, setBackendFilters] = useState<BackendFilters>(
    extractFiltersFromLocalStorage,
  );

  const searchParams = ((): PaymentsSearchParams => {
    const result: BackendFilters = createDefaultBackendFilters();
    if (backendFilters.filters) {
      result.filters = { ...backendFilters.filters };
    }
    if (backendFilters.datagridFilters) {
      result.datagridFilters = [...backendFilters.datagridFilters];
    }
    if (backendFilters.logicalOperator) {
      result.logicalOperator = backendFilters.logicalOperator;
    }

    result.filters = {
      ...result.filters,
      practice_id:
        selectedPractices.length > 0
          ? selectedPractices.map((p) => p.wieldyId)
          : [],
    };

    const datagridFilters: Array<DataGridFilter> = [];
    if (dashboardSettings.selectedDate.startDate) {
      datagridFilters.push({
        field: "payment_date",
        operator: FilterOperator.IS_ON_OR_AFTER,
        value: dashboardSettings.selectedDate.startDate.format("YYYY-MM-DD"),
      });
    }
    if (dashboardSettings.selectedDate.endDate) {
      datagridFilters.push({
        field: "payment_date",
        operator: FilterOperator.IS_ON_OR_BEFORE,
        value: dashboardSettings.selectedDate.endDate.format("YYYY-MM-DD"),
      });
    }

    // Check if user has applied a specific payment_date filter via DataGrid
    const hasPaymentDateFilter = result.datagridFilters?.some(
      (filter) => filter.field === "payment_date",
    );

    // Only add DateRangePicker filters if no specific date filter is applied
    // When user applies a specific date via DataGrid, it takes precedence
    if (datagridFilters.length > 0 && !hasPaymentDateFilter) {
      result.datagridFilters = [
        ...(result.datagridFilters || []),
        ...datagridFilters,
      ];
    }

    return {
      page: pagination.page,
      pageSize: pagination.pageSize,
      filters: result.filters || {},
      datagridFilters: result.datagridFilters,
      logicalOperator: result.logicalOperator,
      devMode: isDevMode,
    };
  })();

  const paymentsQuery = usePayments(searchParams, selectedPractices.length > 0);
  const payments =
    selectedPractices.length > 0
      ? (paymentsQuery.data?.data ?? NO_PAYMENTS)
      : NO_PAYMENTS;
  const paymentsLoading =
    selectedPractices.length > 0 &&
    (paymentsQuery.isPending || paymentsQuery.isPlaceholderData);

  // Reset paging during render so the query never fires with the old page.
  const resetKey = [
    selectedPractices
      .map((p) => p.wieldyId)
      .sort()
      .join(","),
    dashboardSettings.selectedDate.startDate?.format("YYYY-MM-DD"),
    dashboardSettings.selectedDate.endDate?.format("YYYY-MM-DD"),
  ].join("|");
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (prevResetKey !== resetKey) {
    setPrevResetKey(resetKey);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  const handleBackendFilterChange = (filters: BackendFilters) => {
    setBackendFilters(filters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const onPageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  const createPayment = async (
    payment: PaymentMessage,
    fileResponse: FileMessage | null,
  ) => {
    try {
      const wieldyId = await createPaymentMutation.mutateAsync({
        ...payment,
        fileId: fileResponse?.id ?? null,
      });
      const practice = allPractices.find(
        (p) => p.wieldyId === payment.practiceId,
      );
      appendPaymentRow(rqClient, searchParams, {
        ...(payment as PaymentMessage),
        wieldyId,
        practice: practice?.name || "",
        practiceDisplayName: practice?.displayName || "",
        documents: fileResponse ? fileToSource(fileResponse) : null,
      });
      updateSnackBar({
        severity: "success",
        message: `Successfully created payment: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const updatePaymentDeltas = async (
    existingPayment: PaymentMessage,
    payment: PaymentMessage,
    fileResponse: FileMessage | null,
  ) => {
    const deltas = (
      Object.entries(payment) as [keyof PaymentMessage, unknown][]
    ).reduce<Partial<PaymentMessage>>(
      (acc, [key, value]) => {
        const existingValue =
          existingPayment[key] && key === "payer"
            ? (existingPayment[key] as string).toUpperCase()
            : existingPayment[key];
        if (value !== undefined && existingValue !== value) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Partial<PaymentMessage>,
    );
    if (Object.keys(deltas).length === 0 && !fileResponse) {
      updateSnackBar({
        severity: "warning",
        message: `Nothing to update`,
      });
      return;
    }
    try {
      const wieldyId = await updatePaymentMutation.mutateAsync({
        ...deltas,
        wieldyId: payment.wieldyId,
        practiceId: payment.practiceId,
        ...(fileResponse && { fileId: fileResponse?.id }),
      });
      updateStore({
        type: "UPDATE_PAYMENT",
        paymentToUpdate: {
          ...existingPayment,
          ...payment,
          ...(fileResponse && { documents: fileToSource(fileResponse) }),
        },
      });
      updateSnackBar({
        severity: "success",
        message: `Successfully edited payment: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onSavePayment = async (payment: PaymentMessage, file: File | null) => {
    try {
      const fileResponse = file
        ? await saveFile(
            await getClient(),
            payment.practiceId,
            payment.source,
            file,
          )
        : null;
      if (payment.wieldyId) {
        const existingPayment = payments.find(
          (p) => p.wieldyId === payment.wieldyId,
        );
        if (!existingPayment) {
          return;
        }
        await updatePaymentDeltas(existingPayment, payment, fileResponse);
        return;
      }
      await createPayment(payment, fileResponse);
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onSaveAI = async (aiMessage: AIMessage, file: File) => {
    try {
      const fileResponse = await saveFile(
        await getClient(),
        aiMessage.practiceId,
        aiMessage.source,
        file,
      );
      const wieldyId = await createAIEntityMutation.mutateAsync({
        aiMessage,
        fileId: fileResponse.id,
      });
      updateSnackBar({
        severity: "success",
        message: `Successfully created payment. Refresh and look for payment ID: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const validatePayment = async (payment: PaymentMessage) => {
    await validatePaymentAndNotify(
      payment,
      updateStore,
      queryClient,
      updateSnackBar,
    );
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PaymentsListView
            payments={payments}
            paymentsLoading={paymentsLoading}
            selectedPractices={selectedPractices}
            supportedPayers={supportedPayers}
            supportedSources={supportedSources}
            dashboardSettings={dashboardSettings}
            saveDashboardSettings={saveDashboardSettings}
            onSavePayment={onSavePayment}
            onSaveAI={onSaveAI}
            validatePayment={validatePayment}
            onFilterChange={handleBackendFilterChange}
            pagination={{
              ...pagination,
              rowCount: paymentsQuery.data?.total ?? 0,
              totalPages: paymentsQuery.data?.total_pages ?? 1,
              onPageChange,
              onPageSizeChange,
            }}
          />
        }
      />
      <Route
        path="/:paymentId"
        element={
          <PaymentDetail
            selectedPractices={selectedPractices}
            allPractices={allPractices}
            updateSnackBar={updateSnackBar}
            supportedPayers={supportedPayers}
            supportedSources={supportedSources}
          />
        }
      />
    </Routes>
  );
}
